import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { sum } from 'lodash';

import { Field, reduxForm } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Toolbar from '@material-ui/core/Toolbar';
import Paper from '@material-ui/core/Paper';

import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import EditTextField from 'components/fields/EditTextField';
import IconPrint from 'components/icons/IconPrint';
import SwitchField from 'components/fields/SwitchField';

import * as validate from 'utils/validate';

import DocumentPreviewFillTextField from './DocumentPreviewFillTextField';
import DocumentPreviewFillRadioField from './DocumentPreviewFillRadioField';
import DocumentPreviewFillCheckboxField from './DocumentPreviewFillCheckboxField';
import DocumentPreviewFillSignatureField from './DocumentPreviewFillSignatureField';
import DocumentPreviewFillText from './DocumentPreviewFillText';
import DocumentPreviewFillLogo from './DocumentPreviewFillLogo';
import DocumentPreviewFillAutosuggestField from './DocumentPreviewFillAutosuggestField';

const styles = theme => ({
  page: {
    position: 'relative',
    marginTop: 24,
    overflow: 'hidden',
  },
  toolbar: {
    position: 'sticky',
    top: 80,
    zIndex: 20,
    borderBottom: `1px solid ${theme.palette.active[50]}`,
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: -8,
      right: -8,
      top: -40,
      zIndex: 1,
      backgroundColor: theme.palette.grey[100],
    },
  },
  toolbarGrid: {
    position: 'relative',
    zIndex: 2,
  },
  pageNoHeader: {
    marginTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  button: {
    marginLeft: 24,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 8,
    },
  },
  iconButton: {
    marginTop: -4,
    verticalAlign: 'top',
    '$button + &': {
      marginLeft: 8,
    },
  },
  pageImage: {
    width: 'auto',
    height: 'auto',
    width: '100%',
    position: 'relative',
  },
  iconPrint: {
    width: 22,
    height: 20,
  },
  switchField: {
    marginLeft: 16,
    [theme.breakpoints.down('xs')]: {
      marginLeft: -14,
    },
  },
  imageNoBackgroundTemplate: {
    opacity: 0,
  },
  gridItemName: {
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      flexShrink: 0,
    },
  },
});

function onChange(values, dispatch, props, prevValues) {
  if (props.signatureFieldNames && props.signatureFieldNames.length > 0) {
    props.signatureFieldNames.forEach((fieldName) => {
      if (values[fieldName] !== prevValues[fieldName]) {
        props.onSignatureChange(values[fieldName]);
      }
    });
  }
  if (values['Show Charge'] !== prevValues['Show Charge']) {
    if (!values['Show Charge'] && prevValues['Show Charge']) {
      props.document.Pages.forEach((page) => {
        const chargeFields = page.fields ? page.fields.filter(item =>
          item.chargeField) : [];
        chargeFields.forEach((field) => {
          dispatch(props.change(field.name, ''));
        });
      });
    }
    if (values['Show Charge'] && !prevValues['Show Charge']) {
      props.document.Pages.forEach((page) => {
        const chargeFields = page.fields ? page.fields.filter(item =>
          item.chargeField) : [];
        chargeFields.forEach((field) => {
          dispatch(props.change(field.name, props.initialValues[field.name]));
        });
      });
    }
  }
}

class DocumentPreviewFillForm extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      documentWidth: 0,
      fieldsToInitialize: props.document.Pages.map(page => page.fields ? page.fields.length : 0),
    };

    this.onDocumentWidthChange = this.onDocumentWidthChange.bind(this);
  }

  componentDidMount() {
    this.onDocumentWidthChange();
    window.addEventListener('resize', this.onDocumentWidthChange);
    this.renderFieldsInterval = setInterval(() => {
      if (sum(this.state.fieldsToInitialize) > 0) {
        this.setState({ fieldsToInitialize: this.state.fieldsToInitialize.map((item) => {
          if (item > 0) {
            return item - 1;
          } else {
            return item;
          }
        }) });
      } else {
        clearInterval(this.renderFieldsInterval);
      }
    }, 0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onDocumentWidthChange);
    clearInterval(this.renderFieldsInterval);
  }

  onDocumentWidthChange() {
    if (this.documentRef) {
      this.setState({ documentWidth: this.documentRef.getBoundingClientRect().width });
    }
  }

  render() {
    const {
      document,
      handleSubmit,
      submitting,
      onSubmitCustom,
      history,
      record,
      variant,
      business,
      payment,
      showHeader,
      order,
      patient,
      disabled,
      appInfo,
      change,
      classes,
    } = this.props;
    const { documentWidth, fieldsToInitialize } = this.state;
    const lastRecordBeforeDocumentPreviewFill = record.slice().reverse()
      .find(item => item.pathname.indexOf('/print/') === -1);
    const backHref = lastRecordBeforeDocumentPreviewFill
      ? lastRecordBeforeDocumentPreviewFill.pathname : '/';
    const fieldsShift = { x: 0, y: 0 };
    let documentPrintWidth;
    let documentPrintHeight;
    switch (`${document.Format} ${document.Orientation}`) {
      case 'letter p':
        documentPrintWidth = 215.89999999999998;
        documentPrintHeight = 279.4;
        break;
      case 'a4 p':
        documentPrintWidth = 210.0015555555555;
        documentPrintHeight = 297.0000833333333;
        break;
      case 'a4 l':
        documentPrintWidth = 297.0000833333333;
        documentPrintHeight = 210.0015555555555;
        break;
      default:
        documentPrintWidth = 215.89999999999998;
        documentPrintHeight = 279.4;
    }
    return (
      <form
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmitCustom(false))}
      >
        {showHeader &&
          <Toolbar
            className={classes.toolbar}
            disableGutters
          >
            <Grid
              className={classes.toolbarGrid}
              container
              spacing={24}
              alignItems="center"
              justify="space-between"
            >
              <Grid item className={classes.gridItemName}>
                <Field
                  name="Name"
                  component={EditTextField}
                  placeholder="Print Document Name"
                  validate={[validate.required]}
                />
              </Grid>
              <Grid item>
                {document.WithBackgroundTemplate &&
                  <Field
                    id="Show Background Template"
                    name="Show Background Template"
                    label="Show Background Template"
                    component={SwitchField}
                  />
                }
                {document.WithCharge &&
                  <Field
                    className={classes.switchField}
                    id="Show Charge"
                    name="Show Charge"
                    label="Show Charge"
                    component={SwitchField}
                  />
                }
                <Button
                  variant="raised"
                  className={classes.button}
                  onClick={() => {
                    if (variant === '_blank') {
                      window.close();
                    } else {
                      history.push(backHref);
                    }
                  }}
                >
                  {variant === '_blank' ? 'Close' : 'Cancel'}
                </Button>
                <Button
                  variant="raised"
                  color="primary"
                  className={classes.button}
                  disabled={submitting}
                  onClick={handleSubmit(onSubmitCustom(false))}
                >
                  Save
                </Button>
                <IconButton
                  className={classes.iconButton}
                  disabled={submitting || disabled}
                  tooltipContent="Print"
                  onClick={handleSubmit(onSubmitCustom(true))}
                >
                  <IconPrint className={classes.iconPrint} />
                </IconButton>
              </Grid>
            </Grid>
          </Toolbar>
        }
        <div
          className={classes.document}
          ref={(documentRef) => {
            this.documentRef = documentRef;
          }}
        >
          {document.Pages.map((page, index) => (
            <Paper
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={classNames(classes.page, {
                [classes.pageNoHeader]: !showHeader && index === 0,
              })}
            >
              <Field
                id="Background Template"
                name="Show Background Template"
                component={({ input }) => (
                  <img
                    className={classNames(classes.pageImage, {
                      [classes.imageNoBackgroundTemplate]: !input.value,
                    })}
                    src={page.image}
                    alt={document.Name}
                    style={{
                      left: `${(fieldsShift.x / documentPrintWidth) * 100}%`,
                      marginTop: `${(fieldsShift.y / documentPrintHeight) * 100}%`,
                      marginBottom: `-${(fieldsShift.y / documentPrintHeight) * 100}%`,
                    }}
                  />
                )}
              />
              {page.fields
                && page.fields.slice(0, page.fields.length - fieldsToInitialize[index]).map(({ type, ...field }) => field.x ? (
                  <Field
                    key={field.name}
                    component={DocumentPreviewFillTextField}
                    {...type === 'radio' ? {
                      component: DocumentPreviewFillRadioField,
                    } : {}}
                    {...type === 'checkbox' ? {
                      component: DocumentPreviewFillCheckboxField,
                    } : {}}
                    {...type === 'signature' ? {
                      component: DocumentPreviewFillSignatureField,
                      documentWidth,
                    } : {}}
                    {...type === 'text' ? {
                      component: DocumentPreviewFillTextField,
                      type,
                      documentWidth,
                    } : {}}
                    {...type === 'autosuggest' ? {
                      component: DocumentPreviewFillAutosuggestField,
                      type,
                      documentWidth,
                    } : {}}
                    documentPrintWidth={documentPrintWidth}
                    documentPrintHeight={documentPrintHeight}
                    fieldsShift={fieldsShift}
                    {...field}
                    disabled={disabled}
                    business={business}
                    order={order}
                    patient={patient}
                    payment={payment}
                    appInfo={appInfo}
                    change={change}
                  />
                ) : '')}
              {page.Logo && (
                <DocumentPreviewFillLogo
                  documentPrintWidth={documentPrintWidth}
                  documentPrintHeight={documentPrintHeight}
                  business={business}
                  order={order}
                  fieldsShift={fieldsShift}
                  {...page.Logo}
                />
              )}
              {page.texts
                && page.texts.map(item => (
                  <DocumentPreviewFillText
                    key={item.name}
                    {...item}
                    documentPrintWidth={documentPrintWidth}
                    documentPrintHeight={documentPrintHeight}
                    documentWidth={documentWidth}
                    business={business}
                    order={order}
                    patient={patient}
                    payment={payment}
                    appInfo={appInfo}
                    fieldsShift={fieldsShift}
                  />
                ))}
            </Paper>
          ))}
        </div>
      </form>
    );
  }
}

DocumentPreviewFillForm.propTypes = {
  variant: PropTypes.oneOf(['_blank', '_self']),
  handleSubmit: PropTypes.func.isRequired,
  onSubmitCustom: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  document: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }).isRequired,
  record: PropTypes.arrayOf(PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  })).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  showHeader: PropTypes.bool,
  classes: PropTypes.shape({
    page: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    iconButton: PropTypes.string.isRequired,
    iconPrint: PropTypes.string.isRequired,
  }).isRequired,
};

DocumentPreviewFillForm.defaultProps = {
  documentImages: null,
  variant: '_self',
  business: {},
  showHeader: true,
};

const mapStateToProps = store => ({
  record: store.routerParams.record,
});

export default connect(mapStateToProps)(withRouter(reduxForm({
  // enableReinitialize: true,
  destroyOnUnmount: false,
  onChange: (values, dispatch, props, previousValues) => {
    onChange(values, dispatch, props, previousValues);
    if (props.document && typeof props.document.onChange === 'function') {
      props.document.onChange(values, dispatch, props, previousValues);   
    }
  },
})(withStyles(styles)(DocumentPreviewFillForm))));



// WEBPACK FOOTER //
// ./src/components/common/DocumentPreviewFillForm/DocumentPreviewFillForm.js